import React from 'react';
import { useOptimizedImage } from '../../hooks/useOptimizedImage';
import { imageCache } from '../../utils/imageCache';

const TournamentLogo = ({
  tournament,
  size = 'w-10 h-10',
  textSize = 'text-lg',
  className = '',
  showBorder = false,
  borderColor = 'border-white/20',
  fallbackColors = 'from-purple-400 to-indigo-500',
  rounded = 'rounded-lg',
  clickable = false,
  onClick = null
}) => {
  const logoUrl = tournament?.logo;
  const cacheKey = tournament?.id ? imageCache.getTournamentLogoKey(tournament.id, size) : null;
  
  // Check if logo is a valid URL
  const isLogoUrl = (logo) => typeof logo === 'string' && (logo.startsWith('http://') || logo.startsWith('https://'));
  
  // Use optimized image loading only for URL logos, not emojis
  const shouldUseOptimizedLoading = logoUrl && isLogoUrl(logoUrl);
  
  const {
    imageLoaded,
    imageError,
    isVisible,
    imgRef,
    containerRef,
    src,
    onLoad,
    onError
  } = useOptimizedImage(
    shouldUseOptimizedLoading ? logoUrl : null, 
    shouldUseOptimizedLoading ? cacheKey : null, 
    false // No lazy loading for tournament logos
  );

  // Size mapping for Tailwind classes to pixels
  const SIZE_MAP = {
    'w-4 h-4': '16px',
    'w-5 h-5': '20px',
    'w-6 h-6': '24px',
    'w-7 h-7': '28px',
    'w-8 h-8': '32px',
    'w-9 h-9': '36px',
    'w-10 h-10': '40px',
    'w-11 h-11': '44px',
    'w-12 h-12': '48px',
    'w-14 h-14': '56px',
    'w-16 h-16': '64px',
    'w-18 h-18': '72px',
    'w-20 h-20': '80px',
    'w-24 h-24': '96px',
    'w-28 h-28': '112px',
    'w-32 h-32': '128px',
    'w-36 h-36': '144px',
    'w-40 h-40': '160px',
    'w-44 h-44': '176px',
    'w-48 h-48': '192px',
    'w-52 h-52': '208px',
    'w-56 h-56': '224px',
    'w-60 h-60': '240px',
    'w-64 h-64': '256px',
    'w-72 h-72': '288px',
    'w-80 h-80': '320px',
    'w-96 h-96': '384px'
  };

  // Font size mapping for Tailwind classes to pixels (optimized for emojis)
  const FONT_SIZE_MAP = {
    'text-sm': '16px',
    'text-base': '18px', 
    'text-lg': '22px',
    'text-xl': '26px',
    'text-2xl': '32px',
    'text-3xl': '40px',
    'text-4xl': '48px',
    'text-5xl': '56px',
    'text-6xl': '64px',
    'text-7xl': '72px',
    'text-8xl': '96px',
    'text-9xl': '128px'
  };

  // Get pixel values
  const sizeInPx = SIZE_MAP[size] || '40px';
  const fontSizeInPx = FONT_SIZE_MAP[textSize] || '16px';

  // Safety check for tournament
  if (!tournament) {
    return (
      <div 
        className={`flex items-center justify-center text-white font-bold ${className}`}
        style={{
          width: sizeInPx,
          height: sizeInPx,
          borderRadius: rounded === 'rounded-full' ? '50%' : '8px',
          background: 'linear-gradient(135deg, #a78bfa, #6366f1)',
          fontSize: fontSizeInPx
        }}
      >
        🏆
      </div>
    );
  }
  
  // Get fallback - tournament icon or first letter
  const getFallbackLogo = () => {
    // If we have a logo and it's not a URL (so it's an emoji), use it
    if (logoUrl && typeof logoUrl === 'string' && !isLogoUrl(logoUrl)) {
      return logoUrl;
    }
    
    // Use tournament format emoji as fallback
    const formatEmojis = {
      'single_elimination': '🏆',
      'league': '⚽',
      'group_stage_knockout': '🏟️',
      'bo3': '🎯',
      'bo5': '⭐',
      'versus': '🆚',
      'swiss': '🏔️'
    };
    
    // If we have a format emoji, use it
    if (tournament?.format && formatEmojis[tournament.format]) {
      return formatEmojis[tournament.format];
    }
    
    // Otherwise use tournament name initial or default trophy
    return tournament?.name?.charAt(0)?.toUpperCase() || '🏆';
  };

  // Handle click events
  const handleClick = () => {
    if (onClick) {
      onClick(tournament);
    }
  };

  // Common click props
  const clickProps = (clickable || onClick) ? {
    onClick: handleClick,
    className: `${className} cursor-pointer hover:opacity-80 transition-opacity`,
    role: 'button',
    tabIndex: 0,
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick();
      }
    }
  } : { className };

  // Common fallback styles
  const fallbackStyles = {
    width: sizeInPx,
    height: sizeInPx,
    borderRadius: rounded === 'rounded-full' ? '50%' : '8px',
    background: 'linear-gradient(135deg, #a78bfa, #6366f1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: fontSizeInPx,
    fontWeight: 'bold',
    color: 'white',
    lineHeight: '1',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
    ...(showBorder && {
      border: '2px solid rgba(255, 255, 255, 0.2)'
    })
  };

  // Create fallback element
  const FallbackLogo = ({ isLoading = false }) => {
    const logoContent = getFallbackLogo();
    const isEmoji = /\p{Emoji}/u.test(logoContent);
    
    return (
      <div 
        className={`flex items-center justify-center ${isEmoji ? '' : 'text-white font-bold'} ${isLoading ? 'animate-pulse' : ''}`}
        style={{
          ...fallbackStyles,
          ...(isEmoji && {
            fontSize: fontSizeInPx
          })
        }}
        {...clickProps}
      >
        {logoContent}
      </div>
    );
  };

  // Show fallback if: no logo, logo is not a URL (emoji), or image failed to load
  if (!shouldUseOptimizedLoading || imageError) {
    return <FallbackLogo />;
  }

  // Show image with loading fallback using optimized hook
  return (
    <div ref={containerRef} className="relative overflow-hidden" style={{ width: sizeInPx, height: sizeInPx, position: 'relative' }} {...clickProps}>
      {isVisible && (
        <img
          ref={imgRef}
          src={src}
          alt={`${tournament.name || 'Tournament'} logo`}
          className="object-contain"
          onError={onError}
          onLoad={onLoad}
          loading="lazy"
          decoding="async"
          style={{
            width: sizeInPx,
            height: sizeInPx,
            borderRadius: rounded === 'rounded-full' ? '50%' : '8px',
            display: imageError ? 'none' : 'block',
            opacity: imageLoaded ? 1 : 0,
            transition: 'opacity 0.2s ease-in-out',
            ...(showBorder && {
              border: '2px solid rgba(255, 255, 255, 0.2)'
            })
          }}
        />
      )}
      {/* Loading placeholder - only show before image loads */}
      {!imageLoaded && !imageError && src && (
        <div 
          className="absolute inset-0 animate-pulse"
          style={{
            ...fallbackStyles
          }}
        >
          {getFallbackLogo()}
        </div>
      )}
    </div>
  );
};

export default TournamentLogo;